import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { buildSchedule } from './InvoiceSettingDrawer';

const PROJECT_INVOICE_SETTINGS = `
  query ProjectInvoiceSettings($id: String!) {
    findOneProject(id: $id) {
      id
      projectId
      name
      startDate
      deadLine
      deadlineOngoing
      recurrenceFrequency
      recurrenceActive
      seriesRootId
      projectAddress
      purchaseOrderNumber
      attention
      sourceContractId
      invoiceSettings
      projectServices
      tasks {
        title
        description
      }
      booking {
        id
        fullName
        email
        phone
        address
        postcode
        bookingServices {
          id
          frequency
          price
          total
          service {
            id
            service
          }
        }
      }
    }
  }
`;

const INVOICE_COMPANY = `
  query InvoiceSettingsCompany {
    getMyProfile {
      company {
        companyName
        tradingName
        abn
        businessNumber
        companyEmail
        invoicingEmail
        companyPhone
        companyMobile
        unitNumber
        address
        suburb
        state
        postCode
        country
        invoiceBankName
        invoiceAccountName
        invoiceBsb
        invoiceAccountNumber
        invoiceBankReference
      }
    }
  }
`;

const WHITE_LABEL = `
  query InvoiceSettingsBranding {
    getWhiteLabelConfig {
      brand
      email
    }
  }
`;

/** Defaults the White Label page shows before it is filled in. */
const PLACEHOLDER = /your ?company|yourcompany\.com|\(555\)/i;
const real = (value) => {
  const v = String(value ?? '').trim();
  return v && !PLACEHOLDER.test(v) ? v : '';
};

const SOURCE_CONTRACT = `
  query InvoiceSettingsContract($id: String!) {
    findOneClientContract(id: $id) {
      id
      contractNumber
      startDate
      endDate
      frequency
    }
  }
`;

const UPDATE_INVOICE_SETTINGS = `
  mutation UpdateProjectInvoiceSettings($projectId: String!, $settings: JSON!) {
    updateProjectInvoiceSettings(projectId: $projectId, settings: $settings) {
      id
      invoiceSettings
    }
  }
`;

const FREQUENCY_LABEL = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
};

export const PAYMENT_LABEL = {
  bank_transfer: 'Bank Transfer',
  stripe: 'Stripe',
  manual: 'Manual / Other',
};

const pad = (n) => String(n).padStart(2, '0');
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function dateOnly(value) {
  const raw = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '';
}

function text(...values) {
  return values.map((v) => String(v ?? '').trim()).find(Boolean) || '';
}

function companyAddress(c = {}) {
  return [
    [c.unitNumber, c.address].filter(Boolean).join(' '),
    c.suburb,
    [c.state, c.postCode].filter(Boolean).join(' '),
    c.country,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
}

function labeledLine(description, label) {
  const match = String(description || '').match(new RegExp(`^${label}:\\s*(.+)$`, 'im'));
  return String(match?.[1] || '').trim();
}

function serviceLines(project) {
  const snapshot = Array.isArray(project?.projectServices) ? project.projectServices : [];
  const rows = snapshot.length ? snapshot : project?.booking?.bookingServices || [];
  // Project task rows keep "Group:" / "Client-facing scope:" lines; use them
  // when the snapshot task lacks them (older projects lost them on edit).
  const taskRow = new Map(
    (project?.tasks || []).map((t) => [String(t?.title || '').trim().toLowerCase(), t]),
  );
  // Same shape as the invoice: service → task groups → tasks (client-facing
  // scope only; internal work instructions never appear on an invoice).
  return rows.map((row, i) => {
    const byTask =
      String(row?.linePricingMode || row?.pricingMode || '').toLowerCase() === 'task';
    const groups = new Map();
    (Array.isArray(row?.tasks) ? row.tasks : []).forEach((t) => {
      const title = String(t?.title || '').trim();
      if (!title) return;
      const live = taskRow.get(title.toLowerCase())?.description;
      const group =
        String(t?.groupName || labeledLine(live, 'Group')).trim() || 'Tasks';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push({
        title,
        scope: String(
          t?.scope || labeledLine(live, 'Client-facing scope') || t?.description || '',
        ).trim(),
        amount: byTask ? Number(t?.total ?? t?.price ?? 0) || 0 : null,
      });
    });
    return {
      id: row?.id || `svc-${i}`,
      name: row?.service?.service || `Service ${i + 1}`,
      amount: Number(row?.total ?? row?.price ?? 0) || 0,
      groups: [...groups.entries()].map(([name, tasks]) => ({ name, tasks })),
    };
  });
}

const ONE_TIME = /one[\s-]?time|one[\s-]?off|\bonce\b|\bsingle\b/i;

/** Map a project / contract frequency label onto the drawer's options. */
function frequencyFromLabel(label) {
  const n = String(label || '').trim().toLowerCase();
  if (!n || ONE_TIME.test(n)) return null;
  if (n.includes('fortnight')) return { frequency: 'fortnightly' };
  if (/\bweekly\b|every\s*week/.test(n)) return { frequency: 'weekly' };
  if (/\bmonthly\b|every\s*month/.test(n)) return { frequency: 'monthly' };
  if (n.includes('daily')) return { frequency: 'custom', every: 1, unit: 'days' };
  if (n.includes('quarter')) return { frequency: 'custom', every: 3, unit: 'months' };
  return { frequency: 'custom' };
}

/**
 * What the project (and the contract it came from) fixes for invoicing:
 * one-time vs multiple visits, the visit frequency, and the contract dates.
 */
function projectProfileFor(project, contract) {
  const label = String(
    project?.recurrenceFrequency ||
      (Array.isArray(project?.projectServices) && project.projectServices[0]?.frequency) ||
      contract?.frequency ||
      '',
  ).trim();
  const freq = frequencyFromLabel(label);
  const recurring = Boolean(freq) || Boolean(project?.recurrenceActive);
  return {
    label: label || 'One-time',
    projectType: recurring ? 'recurring' : 'one_time',
    frequency: freq?.frequency || (recurring ? 'weekly' : null),
    every: freq?.every ?? null,
    unit: freq?.unit ?? null,
    // Locked unless the project only says "custom" (admin picks the interval).
    frequencyLocked: Boolean(freq && freq.frequency !== 'custom') || Boolean(freq?.every),
    startDate: dateOnly(contract?.startDate) || dateOnly(project?.startDate),
    // Ongoing project: no end date, the calendar rolls forward.
    ongoing: recurring && Boolean(project?.deadlineOngoing),
    endDate: project?.deadlineOngoing
      ? ''
      : dateOnly(contract?.endDate) || dateOnly(project?.deadLine),
    datesFrom: contract?.startDate || contract?.endDate ? 'contract' : 'project',
    contractNumber: contract?.contractNumber || '',
  };
}

/** Saved settings, or sensible defaults from the project when none exist yet. */
function initialSettingsFor(root, profile) {
  const saved =
    root?.invoiceSettings && typeof root.invoiceSettings === 'object'
      ? root.invoiceSettings
      : {};
  const base = {
    ...saved,
    every: saved.every ?? 10,
    unit: saved.unit ?? 'days',
    frequency: saved.frequency || 'weekly',
    startDate: saved.startDate || '',
    endDate: saved.endDate || '',
    overrides: saved.overrides || {},
  };
  if (!profile) return base;
  const next = { ...base, projectType: profile.projectType };
  if (profile.projectType === 'recurring') {
    if (profile.frequencyLocked) {
      next.frequency = profile.frequency;
      if (profile.every) next.every = profile.every;
      if (profile.unit) next.unit = profile.unit;
    } else if (!saved.frequency && profile.frequency) {
      next.frequency = profile.frequency;
    }
    if (profile.startDate) next.startDate = profile.startDate;
    next.ongoing = profile.ongoing;
    if (profile.ongoing) next.endDate = '';
    else if (profile.endDate) next.endDate = profile.endDate;
  }
  return next;
}

/** Next schedule date still to be invoiced (mirrors the backend rule). */
function nextInvoiceDate(settings) {
  if (settings?.projectType !== 'recurring') return null;
  const today = todayISO();
  const generated = settings.generated || {};
  const overrides = settings.overrides || {};
  return (
    buildSchedule(settings).find((date) => {
      if (generated[date]?.invoiceId) return false;
      if (overrides[date]?.status === 'skipped') return false;
      return (overrides[date]?.sendDate || date) >= today;
    }) || null
  );
}

export function frequencyLabel(settings) {
  if (settings?.frequency === 'custom') {
    return `Every ${settings.every || 1} ${settings.unit || 'days'}`;
  }
  return FREQUENCY_LABEL[settings?.frequency] || 'Weekly';
}

/**
 * Invoice Settings for one project: loads the series root (where settings are
 * stored), company + bank details for the preview, and saves the drawer.
 */
export default function useProjectInvoiceSettings(projectId) {
  const [root, setRoot] = useState(null);
  const [current, setCurrent] = useState(null);
  const [company, setCompany] = useState({});
  const [branding, setBranding] = useState({});
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(Boolean(projectId));
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projectRes, companyRes, brandingRes] = await Promise.all([
        graphqlRequest(PROJECT_INVOICE_SETTINGS, { id: projectId }),
        graphqlRequest(INVOICE_COMPANY).catch(() => null),
        graphqlRequest(WHITE_LABEL).catch(() => null),
      ]);
      const project = projectRes?.findOneProject || null;
      let rootProject = project;
      if (project?.seriesRootId && project.seriesRootId !== project.id) {
        const rootRes = await graphqlRequest(PROJECT_INVOICE_SETTINGS, {
          id: project.seriesRootId,
        }).catch(() => null);
        rootProject = rootRes?.findOneProject || project;
      }
      const contractId = rootProject?.sourceContractId || project?.sourceContractId;
      const contractRes = contractId
        ? await graphqlRequest(SOURCE_CONTRACT, { id: contractId }).catch(() => null)
        : null;
      setContract(contractRes?.findOneClientContract || null);
      setCurrent(project);
      setRoot(rootProject);
      setCompany(companyRes?.getMyProfile?.company || {});
      const config = brandingRes?.getWhiteLabelConfig || {};
      setBranding({
        brand: config.brand && typeof config.brand === 'object' ? config.brand : {},
        email: config.email && typeof config.email === 'object' ? config.email : {},
      });
    } catch (err) {
      console.error('Failed to load invoice settings:', err);
      toast.error(err?.message || 'Failed to load invoice settings');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const settings = root?.invoiceSettings || null;

  const invoiceData = useMemo(() => {
    const project = current || root;
    const booking = project?.booking || {};
    const name = text(company.companyName, company.tradingName);
    const brand = branding.brand || {};
    const mail = branding.email || {};
    // Same rules as the emailed PDF (backend invoice-pdf.ts): the issuing
    // business's brand, support email and phone — never the client's.
    const brandName = real(brand.brandName) || name;
    const footer = {
      text:
        real(mail.footerText) ||
        `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`,
      email:
        real(mail.supportEmail) || text(company.invoicingEmail, company.companyEmail),
      phone: real(mail.contactNumber) || text(company.companyPhone, company.companyMobile),
      address: real(mail.address),
      poweredBy: !brand.hidePoweredBy,
    };
    return {
      footer,
      invoiceNo: 'INV—',
      gstRate: 0.1,
      company: {
        name,
        abn: text(company.abn, company.businessNumber),
        address: companyAddress(company),
        phone: text(company.companyPhone, company.companyMobile),
        email: text(company.invoicingEmail, company.companyEmail),
      },
      client: {
        name: booking.fullName || '',
        address: [booking.address, booking.postcode].filter(Boolean).join(', '),
        projectAddress: project?.projectAddress || booking.address || '',
        phone: booking.phone || '',
        email: booking.email || '',
      },
      project: {
        name: project?.name || '',
        code: project?.projectId || '',
        attention: project?.attention || root?.attention || '',
        purchaseOrderNumber:
          project?.purchaseOrderNumber || root?.purchaseOrderNumber || '',
      },
      lines: serviceLines(project),
      bank: {
        bankName: company.invoiceBankName || '',
        accountName: company.invoiceAccountName || name,
        bsb: company.invoiceBsb || '',
        accountNumber: company.invoiceAccountNumber || '',
      },
    };
  }, [branding, company, current, root]);

  const profile = useMemo(
    () => (root ? projectProfileFor(root, contract) : null),
    [root, contract],
  );
  const initialSettings = useMemo(
    () => initialSettingsFor(root, profile),
    [root, profile],
  );
  const sentDates = useMemo(
    () => Object.keys(settings?.generated || {}),
    [settings],
  );
  const autoInvoiceIds = useMemo(
    () =>
      new Set(
        Object.values(settings?.generated || {})
          .map((row) => row?.invoiceId)
          .filter(Boolean),
      ),
    [settings],
  );

  const summary = useMemo(() => {
    if (!settings?.projectType) return null;
    const recurring = settings.projectType === 'recurring';
    const subtotal = invoiceData.lines.reduce((s, l) => s + Number(l.amount || 0), 0);
    const next = recurring ? nextInvoiceDate(settings) : null;
    const override = next ? settings.overrides?.[next] : null;
    return {
      recurring,
      frequency: recurring ? frequencyLabel(settings) : 'One-time',
      nextInvoice: next,
      nextAmount:
        override?.status === 'edited' && override.amount != null && override.amount !== ''
          ? Number(override.amount)
          : subtotal * 1.1,
      payment: PAYMENT_LABEL[settings.paymentMode] || 'Bank Transfer',
      dueDays: settings.dueDays ?? 15,
    };
  }, [settings, invoiceData]);

  const save = useCallback(
    async (next) => {
      const targetId = root?.id || projectId;
      if (!targetId) return false;
      setSaving(true);
      try {
        await graphqlRequest(UPDATE_INVOICE_SETTINGS, {
          projectId: targetId,
          settings: next,
        });
        toast.success('Invoice settings saved');
        await reload();
        return true;
      } catch (err) {
        toast.error(err?.message || 'Failed to save invoice settings');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [projectId, reload, root?.id],
  );

  return {
    loading,
    saving,
    save,
    reload,
    project: current,
    root,
    settings,
    summary,
    invoiceData,
    initialSettings,
    profile,
    sentDates,
    autoInvoiceIds,
  };
}
